import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	arrayMutators,
	DurationInputField,
	FieldArray,
	Form,
	FormSpy,
	GetFieldValue,
	isTruthy,
	MarkdownInputField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Markdown } from "@sembark-travel/ui-markdown"

import { Optional, Required } from "utility-types"
import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import { TTripDestination } from "../TripDestinations"
import * as Validator from "yup"
import { useRef } from "react"
import { TTravelActivityTicketTouristConfiguration } from "./store"
import {
	SelectClosedOnDateIntervalsField,
	SelectClosedOnDaysOfWeekField,
} from "./NewItem"

type TAddActivityTicketTypesFormValues = {
	name: string
	description?: string
	trip_destinations?: Array<TTripDestination>
	ticket_tourist_configurations: Array<TTravelActivityTicketTouristConfiguration>
	ticket_types: Array<{
		name: string
		short_name?: string
		description?: string
		closed_on_days_of_week?: Array<string>
		closed_on_date_intervals?: Array<{
			start_date?: Date
			end_date?: Date
		}>
		durations?: Array<{
			iso?: string
			slots?: string
		}>
	}>
}

const INITIAL_VALUES: TAddActivityTicketTypesFormValues = {
	name: "",
	description: "",
	trip_destinations: [],
	ticket_tourist_configurations: [],
	ticket_types: [
		{
			name: "",
			short_name: "",
			description: "",
			durations: [
				{
					iso: "",
					slots: "",
				},
			],
		},
	],
}

export function AddActivityTicketTypesForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues = {},
	canAddMultiple = true,
}: {
	onSubmit: (data: {
		ticket_types: Array<{
			name: string
			short_name?: string
			description?: string
			closed_on_days_of_week?: Array<string>
			closed_on_date_intervals?: Array<[string, string]>
			durations?: Array<{
				iso: string | null
				slots?: Array<string>
			}>
		}>
	}) => Promise<void>
	onCancel?: () => void
	initialValues?: Optional<TAddActivityTicketTypesFormValues>
	canAddMultiple?: boolean
}) {
	const validationSchema = Validator.object().shape({
		ticket_types: Validator.array()
			.of(
				Validator.object().shape({
					name: Validator.string()
						.required("Please provide a name for the type")
						.max(191, "Please use 191 or lesser characters for the name."),
					short_name: Validator.string()
						.nullable()
						.max(
							191,
							"Please use 191 or lesser characters for the short_name."
						),
					description: Validator.string()
						.nullable()
						.max(10000, "Please use 10000 or less characters for the details."),
				})
			)
			.min(1, "Please add at least one ticket type"),
	})

	const DEFAULT_DURATION = {
		iso: "",
		slots: "",
	}

	const DEFAULT_TICKET_TYPE = {
		name: "",
		short_name: "",
		description: "",
		durations: [DEFAULT_DURATION],
	}

	const initialValues = useRef<TAddActivityTicketTypesFormValues>({
		...INITIAL_VALUES,
		...propInitialValues,
		ticket_types:
			propInitialValues.ticket_types?.map((ticket) => ({
				...DEFAULT_TICKET_TYPE,
				...ticket,
				durations: ticket.durations?.length
					? ticket.durations
					: [...DEFAULT_TICKET_TYPE.durations],
			})) || INITIAL_VALUES.ticket_types,
	})

	return (
		<Form<TAddActivityTicketTypesFormValues>
			initialValues={initialValues.current}
			validate={validateFormValues(validationSchema)}
			onSubmit={withServerErrors(async (values) => {
				const data = {
					ticket_types:
						values.ticket_types?.map((s) => ({
							name: s.name,
							short_name: s.short_name,
							description: s.description,
							closed_on_days_of_week: s.closed_on_days_of_week,
							closed_on_date_intervals: (s.closed_on_date_intervals || [])
								.filter((d): d is Required<typeof d> =>
									Boolean(d.start_date || d.end_date)
								)
								.map((d) => {
									const start_date = d.start_date
										? formatDate(d.start_date, "YYYY-MM-DD")
										: undefined

									const end_date = d.end_date
										? formatDate(d.end_date, "YYYY-MM-DD")
										: undefined

									if (start_date || end_date) {
										return [start_date || end_date, end_date || start_date] as [
											string,
											string,
										]
									}

									return undefined
								})
								.filter(
									(d): d is Exclude<typeof d, undefined> => d !== undefined
								),

							durations: s.durations
								? s.durations
										.map((d) => ({
											iso: d.iso || null,
											slots: (d.slots || "")
												.split(",")
												.map((s) => s.trim())
												.filter(Boolean)
												.map((s) => {
													try {
														const date = parseDate(s, "HH:mm")

														return formatDate(date, "HH:mm")
													} catch (e) {
														return s
													}
												}),
										}))
										.filter(Boolean)
								: undefined,
						})) || [],
				}

				await onSubmit(data)
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading fontSize="md">Activity Details</Heading>
								<Text color="muted">Basic details regarding the activity.</Text>
							</Stack>
						</Col>

						<Col>
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Box>
										<Text fontWeight="semibold">Name</Text>

										<Text color="muted" fontWeight="semibold">
											{propInitialValues.name || "-"}
										</Text>
									</Box>
								</Col>

								{propInitialValues?.ticket_tourist_configurations && (
									<Col sm={12} md={4}>
										<Box>
											<Text fontWeight="semibold">
												Tourist Age Configuration
											</Text>

											<Text color="muted">
												{propInitialValues.ticket_tourist_configurations
													?.map((t) => t?.name)
													.join(", ") || "-"}
											</Text>
										</Box>
									</Col>
								)}

								{propInitialValues?.trip_destinations && (
									<Col sm={12} md={4}>
										<Box>
											<Text fontWeight="semibold">Trip Destinations</Text>

											<Text color="muted">
												{propInitialValues.trip_destinations
													?.map((d) => d.name)
													.join(", ") || "All"}
											</Text>
										</Box>
									</Col>
								)}

								{propInitialValues?.description && (
									<Col>
										<Box>
											<Text fontWeight="semibold">Itinerary / Details</Text>

											<Box color="muted">
												{propInitialValues.description ? (
													<Markdown>{propInitialValues.description}</Markdown>
												) : (
													"-"
												)}
											</Box>
										</Box>
									</Col>
								)}
							</Grid>
						</Col>
					</Grid>

					<Divider sm />
					<Grid gap="4">
						<Col sm={12} md={4}>
							<Stack gap="1">
								<Heading fontSize="md">
									{canAddMultiple
										? "Ticket Type/Packages"
										: "Ticket Type/Package"}
								</Heading>

								<Text color="muted">
									{canAddMultiple
										? "Please provide the ticket types or packages available for this activity."
										: "Please provide the ticket type or package available for this activity."}
								</Text>
							</Stack>
						</Col>

						<Col>
							<Box paddingY="2">
								<FieldArray<
									Required<
										TAddActivityTicketTypesFormValues,
										"ticket_types"
									>["ticket_types"][number]
								> name="ticket_types">
									{({ fields }) => (
										<Stack gap="2">
											{fields.map((name, index) => (
												<Stack
													key={name}
													paddingLeft="4"
													borderBottomWidth="1"
													borderLeftColor="emphasis"
													paddingBottom="2"
													borderLeftWidth="2"
													gap="4"
												>
													<Inline gap="6" flexWrap="wrap" collapseBelow="sm">
														<Inline
															flex="1"
															justifyContent="between"
															flexWrap="wrap"
															gap="1"
														>
															<Inline gap="1" flex="1">
																<Box flex="1">
																	<Stack gap="4">
																		<TextInputField
																			name={`${name}.name`}
																			label="Name"
																			placeholder="e.g.  Premium"
																			type="text"
																		/>

																		<TextInputField
																			name={`${name}.short_name`}
																			label="Internal Ref Code"
																			secondaryLabel="optional"
																			placeholder="e.g. 1PXABC"
																			maxWidth="md"
																		/>
																	</Stack>
																</Box>
																{canAddMultiple && (
																	<Box paddingTop="6">
																		<Button
																			onClick={() => fields.remove(index)}
																			title="Remove this ticket type from the activity"
																			level="tertiary"
																			size="sm"
																		>
																			<Icons.Cancel />
																		</Button>
																	</Box>
																)}
															</Inline>
															{canAddMultiple &&
															index === (fields.length || 0) - 1 ? (
																<Box paddingTop="6">
																	<Button
																		onClick={() =>
																			fields.push({
																				name: "",
																				durations: [
																					{
																						iso: undefined,
																						slots: undefined,
																					},
																				],
																			})
																		}
																	>
																		Add More Ticket/Package Types
																	</Button>
																</Box>
															) : null}
														</Inline>

														<FieldArray<
															Required<
																Required<TAddActivityTicketTypesFormValues>["ticket_types"][number]
															>["durations"][number]
														>
															name={`${name}.durations`}
														>
															{({ fields }) => (
																<Stack gap="4">
																	{fields.map((name, index) => (
																		<Inline gap="4" key={name}>
																			<Inline gap="4" flex="1" flexWrap="wrap">
																				<TextInputField
																					label="Slots"
																					type="text"
																					name={`${name}.slots`}
																					placeholder="11:00, 13:00"
																					secondaryLabel="optional"
																					help={
																						<GetFieldValue<string>
																							name={`${name}.slots`}
																						>
																							{({ value }) => {
																								if (!value) return null

																								const slots = value
																									.split(",")
																									.map((s) => s.trim())
																								const parsedSlots = slots.map(
																									(slot) => {
																										try {
																											const date = parseDate(
																												slot,
																												"HH:mm"
																											)

																											return formatDate(
																												date,
																												"HH:mm"
																											)
																										} catch (e) {
																											return "Invalid Time"
																										}
																									}
																								)

																								const validSlots =
																									parsedSlots.filter(Boolean)

																								return (
																									<Inline>
																										{validSlots.length > 0 && (
																											<Text color="success">
																												{validSlots.join(", ")}
																											</Text>
																										)}
																									</Inline>
																								)
																							}}
																						</GetFieldValue>
																					}
																				/>
																				<DurationInputField
																					name={`${name}.iso`}
																					label="Duration"
																					secondaryLabel="optional"
																				/>
																			</Inline>
																			<Inline gap="2" paddingTop="6">
																				{Number(fields.length || 0) > 1 ? (
																					<Button
																						onClick={() => fields.remove(index)}
																						size="sm"
																					>
																						<Icons.Cancel />
																					</Button>
																				) : null}
																			</Inline>
																		</Inline>
																	))}
																</Stack>
															)}
														</FieldArray>
													</Inline>
													<MarkdownInputField
														name={`${name}.description`}
														label="Itinerary/Details"
														secondaryLabel="optional"
														placeholder="Some details regarding this ticket type"
													/>
													<GetFieldValue<
														"" | boolean
													> name="same_closing_days_and_intervals_for_all_tickets">
														{({ value }) =>
															isTruthy(value) ? null : (
																<>
																	<SelectClosedOnDaysOfWeekField
																		name={`${name}.closed_on_days_of_week`}
																	/>
																	<SelectClosedOnDateIntervalsField
																		name={`${name}.closed_on_date_intervals`}
																	/>
																</>
															)
														}
													</GetFieldValue>
												</Stack>
											))}
											{!fields.length ? (
												<Box paddingTop="6">
													<Button
														onClick={() =>
															fields.push({
																name: "",
																durations: [
																	{
																		iso: undefined,
																		slots: undefined,
																	},
																],
															})
														}
													>
														Add More Ticket/Package Types
													</Button>
												</Box>
											) : null}
										</Stack>
									)}
								</FieldArray>
							</Box>
						</Col>
					</Grid>

					<Divider sm />

					<Box
						padding="4"
						boxShadow="lg"
						rounded="lg"
						borderWidth="1"
						bgColor="subtle"
						marginY="4"
					>
						<Box
							textTransform="uppercase"
							fontSize="sm"
							color="muted"
							marginBottom="4"
							fontWeight="semibold"
							letterSpacing="wider"
						>
							Summary
						</Box>

						<Stack gap="2">
							<FormSpy<TAddActivityTicketTypesFormValues>>
								{({ values }) => {
									const { name, ticket_types } = values

									return ticket_types?.map((t, index) => (
										<Stack key={index} gap="1">
											<Box>
												<Heading>
													{name}
													{t.name ? (
														<Box as="span" marginLeft="1">
															- {t.name}
														</Box>
													) : null}
												</Heading>
											</Box>

											<Box color="muted">
												{t.description ? (
													<Markdown>{t.description}</Markdown>
												) : (
													"[Ticket Itinerary]"
												)}
											</Box>
										</Stack>
									))
								}}
							</FormSpy>
						</Stack>
					</Box>

					<Divider sm />

					<Grid gap="4">
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4" paddingLeft={{ md: "4" }}>
								<SubmissionError />

								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Please wait" : "Save Details"}
									</Button>

									{onCancel ? (
										<Button
											onClick={() => onCancel()}
											disabled={submitting}
											level="tertiary"
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}
